import MobileMenuToggler from 'components/mobileMenuToggler/MobileMenuToggler';
import useCountryContext from 'core/hooks/useCountryContext';
import useDarkMode from 'core/hooks/useDarkMode';
import usePermissions from 'core/hooks/usePermissions';
import { useState } from 'react';
import { generatePath, Link, NavLink } from 'react-router-dom';
import routes from 'routes';

const Header = () => {
  const [isDarkMode, toggleTheme] = useDarkMode();
  const { countryContext } = useCountryContext(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { hasPermission } = usePermissions();
  const countryCodeParam = countryContext?.toLowerCase() ?? null;

  const handleMenuTogglerClick = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleThemeToggle = (darkMode: boolean) => {
    toggleTheme(darkMode);
    setIsMenuOpen(false);
  };

  return (
    <nav className="border-b border-sublte-white dark:border-design-secondary-black">
      <div className="flex h-16 flex-wrap items-center justify-between p-2 dark:bg-design-black">
        <Link
          to={generatePath(':countryCode', {
            countryCode: countryCodeParam,
          })}
          className="flex items-center gap-4 font-semibold"
        >
          <img src="/sameday_logo.png" alt="Sameday logo" className="h-10" />
          <span className="hover:text-design-red dark:text-white">
            Itinerary Viewer
          </span>
        </Link>
        <div className="inline-flex xl:hidden">
          <MobileMenuToggler onClickHandler={handleMenuTogglerClick} />
        </div>
        <div
          className={`${
            isMenuOpen ? 'block' : 'hidden'
          } z-10 mt-4 w-full items-center bg-white dark:bg-design-black xl:mt-0 xl:flex xl:w-auto xl:gap-2 xl:bg-inherit`}
        >
          <Link to="country-selection">
            <span className="mx-2 flex h-10 w-10 items-center justify-center rounded-full hover:cursor-pointer">
              <img
                src={`/${countryContext}.png`}
                alt={countryContext}
                className="h-6"
              />
            </span>
          </Link>
          <ul className="flex flex-col px-4 xl:flex-row xl:gap-2 xl:p-0">
            {routes
              .filter(
                (x) =>
                  x.showInMenu &&
                  (x.permissionsRequired === undefined ||
                    hasPermission(x.permissionsRequired)),
              )
              .sort((a, b) => (a.order > b.order ? 1 : -1))
              .map((route) => (
                <li key={route.path} className="py-2 xl:py-0">
                  <NavLink
                    to={generatePath(route.path, {
                      countryCode: countryCodeParam,
                    })}
                    end={route.exactMatch}
                    className={({ isActive }) =>
                      `dark:text-white dark:hover:text-white-contrast hover:text-black${
                        isActive ? ' text-red dark:text-red' : ''
                      }`
                    }
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {route.displayName}
                  </NavLink>
                </li>
              ))}
          </ul>
          <div
            className="flex w-full rounded-lg bg-design-black-accent p-1 xl:w-auto xl:p-0"
            role="group"
          >
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium xl:w-auto ${
                isDarkMode
                  ? 'bg-design-black-accent text-white '
                  : 'bg-white text-design-black'
              } rounded-s-lg flex w-1/2 justify-center`}
              onClick={() => handleThemeToggle(false)}
              aria-label="Light mode"
            >
              <svg
                height="14"
                viewBox="0 0 16 17"
                className="fill-design-black-accent dark:fill-white"
              >
                <path d="M7.57789 5.23145C6.93068 5.23145 6.29802 5.4339 5.75988 5.81323C5.22175 6.19256 4.80231 6.7317 4.55464 7.3625C4.30696 7.9933 4.24216 8.68739 4.36842 9.35704C4.49468 10.0267 4.80636 10.6418 5.264 11.1246C5.72165 11.6074 6.30473 11.9362 6.9395 12.0694C7.57427 12.2026 8.23222 12.1342 8.83016 11.8729C9.4281 11.6117 9.93917 11.1692 10.2987 10.6015C10.6583 10.0338 10.8503 9.36634 10.8503 8.68357C10.8478 7.76879 10.5023 6.89221 9.88912 6.24536C9.27595 5.59851 8.44503 5.23399 7.57789 5.23145Z" />
                <path d="M7.57809 4.16324C7.66658 4.16395 7.75434 4.14606 7.83622 4.11062C7.91811 4.07517 7.99254 4.02287 8.05511 3.95677C8.11769 3.89066 8.16716 3.81207 8.20071 3.72557C8.23426 3.63906 8.25122 3.54637 8.25054 3.45288V1.54788C8.25054 1.35948 8.17967 1.1788 8.05357 1.04558C7.92746 0.912364 7.75643 0.837524 7.57809 0.837524C7.39975 0.837524 7.22872 0.912364 7.10261 1.04558C6.97651 1.1788 6.9057 1.35948 6.9057 1.54788V3.46467C6.90663 3.55692 6.92475 3.64807 6.95906 3.73291C6.99337 3.81775 7.04319 3.89462 7.10565 3.9591C7.1681 4.02359 7.24198 4.07443 7.32305 4.10872C7.40412 4.14301 7.49076 4.16009 7.57809 4.15895V4.16324Z" />
                <path d="M3.60084 5.49606C3.72502 5.62957 3.8938 5.70648 4.07107 5.71034C4.15942 5.7122 4.24712 5.69403 4.32825 5.65706C4.40937 5.6201 4.48202 5.56519 4.5413 5.49606C4.60449 5.43189 4.65475 5.35492 4.68908 5.26978C4.72342 5.18463 4.74111 5.09304 4.74111 5.00052C4.74111 4.90799 4.72342 4.81641 4.68908 4.73127C4.65475 4.64612 4.60449 4.56916 4.5413 4.50499L3.25756 3.15069C3.13298 3.01927 2.964 2.94543 2.78782 2.94543C2.61164 2.94543 2.44268 3.01927 2.3181 3.15069C2.19352 3.28212 2.12354 3.46038 2.12354 3.64624C2.12354 3.8321 2.19352 4.01034 2.3181 4.14177L3.60084 5.49606Z" />
                <path d="M3.29306 8.6847C3.29374 8.59122 3.27678 8.49858 3.24318 8.41207C3.20958 8.32557 3.16001 8.24694 3.09735 8.18084C3.03469 8.11473 2.96019 8.06241 2.87819 8.02696C2.79619 7.99152 2.70832 7.97367 2.6197 7.97439H0.813924C0.635338 7.97439 0.464065 8.04919 0.337785 8.18241C0.211506 8.31562 0.140564 8.49631 0.140564 8.6847C0.140564 8.8731 0.211506 9.05378 0.337785 9.187C0.464065 9.32022 0.635338 9.39509 0.813924 9.39509H2.63088C2.71833 9.39411 2.80473 9.37496 2.88515 9.33872C2.96557 9.30247 3.03843 9.24984 3.09956 9.18386C3.16069 9.11788 3.20888 9.03984 3.24139 8.9542C3.27389 8.86855 3.29008 8.77696 3.289 8.6847H3.29306Z" />
                <path d="M3.60081 11.8732L2.31705 13.2275C2.25387 13.2917 2.20361 13.3687 2.16927 13.4538C2.13494 13.539 2.11725 13.6305 2.11725 13.723C2.11725 13.8156 2.13494 13.9071 2.16927 13.9923C2.20361 14.0774 2.25387 14.1544 2.31705 14.2186C2.44123 14.3521 2.61002 14.429 2.78729 14.4328C2.87563 14.4346 2.96332 14.4164 3.04443 14.3795C3.12554 14.3425 3.19821 14.2877 3.25753 14.2186L4.54128 12.8643C4.60446 12.8001 4.65472 12.7232 4.68906 12.638C4.72339 12.5529 4.74108 12.4613 4.74108 12.3688C4.74108 12.2763 4.72339 12.1846 4.68906 12.0995C4.65472 12.0143 4.60446 11.9374 4.54128 11.8732C4.41453 11.7458 4.24615 11.6747 4.07104 11.6747C3.89594 11.6747 3.72755 11.7458 3.60081 11.8732Z" />
                <path d="M7.57811 13.2103C7.48962 13.2096 7.40192 13.2275 7.32004 13.263C7.23815 13.2984 7.16372 13.3507 7.10115 13.4168C7.03857 13.4829 6.98904 13.5615 6.95549 13.648C6.92194 13.7345 6.90505 13.8272 6.90572 13.9207V15.8375C6.90572 16.0259 6.97653 16.2066 7.10263 16.3398C7.22874 16.473 7.39977 16.5478 7.57811 16.5478C7.75645 16.5478 7.92748 16.473 8.05358 16.3398C8.17969 16.2066 8.25056 16.0259 8.25056 15.8375V13.909C8.24963 13.8167 8.23151 13.7256 8.1972 13.6407C8.16289 13.5559 8.11307 13.479 8.05061 13.4145C7.98816 13.35 7.91428 13.2992 7.83321 13.2649C7.75214 13.2306 7.66543 13.2135 7.57811 13.2147V13.2103Z" />
                <path d="M11.5563 11.8754C11.4954 11.8087 11.4224 11.7557 11.3417 11.7195C11.261 11.6833 11.1742 11.6646 11.0865 11.6646C10.9988 11.6646 10.912 11.6833 10.8313 11.7195C10.7506 11.7557 10.6776 11.8087 10.6167 11.8754C10.4926 12.0071 10.423 12.1852 10.423 12.3709C10.423 12.5566 10.4926 12.7347 10.6167 12.8664L11.9005 14.2208C12.0247 14.3543 12.1935 14.4311 12.3708 14.435C12.4591 14.4368 12.5468 14.4186 12.6279 14.3816C12.709 14.3447 12.7816 14.2898 12.841 14.2208C12.9042 14.1566 12.9544 14.0796 12.9888 13.9944C13.0231 13.9093 13.0408 13.8177 13.0408 13.7252C13.0408 13.6327 13.0231 13.5411 12.9888 13.456C12.9544 13.3708 12.9042 13.2938 12.841 13.2296L11.5563 11.8754Z" />
                <path d="M14.344 7.97437H12.5281C12.3495 7.97437 12.1783 8.04917 12.052 8.18239C11.9257 8.3156 11.8547 8.49629 11.8547 8.68468C11.8547 8.87308 11.9257 9.05376 12.052 9.18698C12.1783 9.3202 12.3495 9.39507 12.5281 9.39507H14.344C14.5226 9.39507 14.6939 9.3202 14.8202 9.18698C14.9464 9.05376 15.0174 8.87308 15.0174 8.68468C15.0174 8.49629 14.9464 8.3156 14.8202 8.18239C14.6939 8.04917 14.5226 7.97437 14.344 7.97437Z" />
                <path d="M11.087 5.69427C11.1753 5.69613 11.263 5.67796 11.3441 5.64099C11.4253 5.60403 11.4979 5.54913 11.5572 5.47999L12.8409 4.1257C12.9655 3.99427 13.0355 3.81603 13.0355 3.63017C13.0355 3.44431 12.9655 3.26606 12.8409 3.13464C12.7163 3.00322 12.5474 2.92938 12.3712 2.92938C12.195 2.92938 12.0261 3.00322 11.9015 3.13464L10.6177 4.48892C10.5545 4.55309 10.5042 4.63005 10.4699 4.7152C10.4356 4.80035 10.4179 4.89193 10.4179 4.98446C10.4179 5.07699 10.4356 5.16856 10.4699 5.25371C10.5042 5.33886 10.5545 5.41582 10.6177 5.47999C10.6769 5.54899 10.7494 5.60382 10.8303 5.64078C10.9113 5.67774 10.9988 5.69598 11.087 5.69427Z" />
              </svg>
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium xl:w-auto ${
                isDarkMode
                  ? 'bg-white text-design-black'
                  : 'bg-design-black-accent text-white'
              } rounded-e-lg flex w-1/2 justify-center`}
              onClick={() => handleThemeToggle(true)}
              aria-label="Dark Mode"
            >
              <svg
                height="14"
                viewBox="0 0 15 15"
                className="fill-white dark:fill-design-black-accent"
              >
                <path d="M13.7432 10.0646C13.585 9.89581 13.3769 9.78917 13.1533 9.76247C12.9297 9.73577 12.7042 9.7906 12.5143 9.91786C11.5269 10.5548 10.3651 10.8228 9.21577 10.6788C8.06648 10.5347 6.99656 9.98689 6.17817 9.12354C5.35978 8.26018 4.84052 7.13147 4.70394 5.91903C4.56736 4.70659 4.82142 3.48094 5.42522 2.43929C5.54623 2.23951 5.59922 2.00224 5.57549 1.76648C5.55175 1.53072 5.45271 1.31049 5.29462 1.14198C5.13654 0.973464 4.92882 0.86669 4.7056 0.839201C4.48238 0.811712 4.25693 0.86514 4.06631 0.990712C2.91433 1.72721 1.98407 2.79341 1.38361 4.06544C0.783146 5.33747 0.537125 6.76312 0.674123 8.17678C0.852497 9.90241 1.58394 11.5121 2.74745 12.7395C3.91096 13.967 5.4368 14.7386 7.07256 14.9268C7.31224 14.9525 7.55092 14.9621 7.7835 14.9621C9.00904 14.9656 10.2146 14.6347 11.2832 14.0017C12.3518 13.3687 13.2471 12.455 13.8823 11.3493C14.0051 11.1513 14.058 10.914 14.0319 10.679C14.0057 10.444 13.9021 10.2262 13.7391 10.0636L13.7432 10.0646Z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;
